import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, CheckCheck, ChevronRight } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification: any) => {
    // Mark as read first
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    
    // Close dropdown before navigation
    setIsOpen(false);
    
    // Navigate to action URL if provided (check data property)
    const actionUrl = notification.data && notification.data.actionUrl;
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const notificationDate = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Nu';
    if (diffInMinutes < 60) return `${diffInMinutes}m geleden`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}u geleden`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d geleden`;
    
    return notificationDate.toLocaleDateString('nl-NL');
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'event_approved':
      case 'group_join_accepted':
        return '🎉';
      case 'event_rejected':
      case 'group_join_rejected':
        return '❌';
      case 'event_unpublished':
        return '⚠️';
      case 'event_cancelled':
        return '🚫';
      case 'event_created':
        return '📅';
      case 'group_request_received':
      case 'group_join_request':
        return '👥';
      case 'group_member_joined':
        return '👋';
      default:
        return '🔔';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-fg-nav hover:text-fg-bright transition-colors duration-200 focus:outline-none rounded-xl"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 bg-neon-cyan shadow-neon-cyan rounded-full h-2 w-2"></span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-surface rounded-card shadow-panel border border-white/[.09] z-50 max-h-96 overflow-hidden animate-slide-down">
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/[.08]">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-fg-bright">
                Notificaties {unreadCount > 0 && `(${unreadCount})`}
              </h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-neon-cyan hover:opacity-80 font-semibold flex items-center"
                  >
                    <CheckCheck className="h-3 w-3 mr-1" />
                    Markeer gelezen
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-fg-muted hover:text-fg-bright"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {!notifications || notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-fg-muted">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Geen notificaties</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[.06]">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 hover:bg-white/[.04] cursor-pointer transition-colors duration-200 ${
                      !notification.isRead ? 'border-l-4 border-l-neon-cyan bg-neon-cyan/[.05]' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center">
                          <span className="text-lg mr-2" role="img" aria-label="notification-icon">
                            {getNotificationIcon(notification.type)}
                          </span>
                          <p className={`text-sm font-medium ${!notification.isRead ? 'text-fg-bright' : 'text-fg-muted'}`}>
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <div className="ml-2 w-2 h-2 bg-neon-cyan shadow-neon-cyan rounded-full"></div>
                          )}
                        </div>
                        <p className="mt-1 text-xs font-mono text-fg-meta">
                          {formatTimeAgo(notification.createdAt)}
                        </p>
                      </div>

                      <div className="ml-2 flex-shrink-0 flex items-center space-x-1">
                        {/* Show chevron if notification is clickable */}
                        {(notification.data && notification.data.actionUrl) && (
                          <ChevronRight className="h-3 w-3 text-fg-muted opacity-50" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="text-fg-muted hover:text-neon-pink-soft transition-colors duration-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-white/[.08]">
              <button
                onClick={() => {
                  // Could navigate to a full notifications page in the future
                  setIsOpen(false);
                }}
                className="text-xs text-neon-cyan hover:opacity-80 font-semibold w-full text-center"
              >
                Alle notificaties bekijken
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;